import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import {
  FileText,
  Mail,
  CheckCircle2,
  Trophy,
  Copy,
  Paperclip,
  ChevronDown,
  ChevronUp,
  FilePlus2,
} from "lucide-react";
import PedidoAnexos from "./PedidoAnexos";
import PropostaNativaForm from "./PropostaNativaForm";

const STATUS_LABEL = {
  rascunho: { label: "Rascunho", color: "bg-zinc-500" },
  enviado: { label: "Enviado ao cliente", color: "bg-blue-600" },
  aprovado: { label: "Aprovado", color: "bg-purple-600" },
  assinado: { label: "Assinado", color: "bg-amber-500" },
  concluido: { label: "Concluído", color: "bg-green-600" },
  cancelado: { label: "Cancelado", color: "bg-red-600" },
};

const TIPO_LABEL = { "12_meses": "12 meses", mensal: "Mensal", renovacao: "Renovação", venda: "Venda" };

const fmtMoney = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

const fmtData = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export default function PedidosLead({ leadId, lead, onChange }) {
  const [items, setItems] = useState([]);
  const [anexosOpenId, setAnexosOpenId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/comercial/pedidos", { params: { lead_id: leadId } });
    setItems(data.items || []);
  };

  useEffect(() => {
    load();
    /* eslint-disable-next-line */
  }, [leadId]);

  const enviar = async (pid) => {
    try {
      const { data } = await api.post(`/comercial/pedidos/${pid}/enviar`);
      toast.success(`Enviado ao cliente (${data.email_mode})`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    }
  };

  const concluir = async (pid) => {
    if (!window.confirm("Concluir pedido? Será gerado contrato + comissão e Financeiro/Logística serão notificados.")) return;
    try {
      const { data } = await api.post(`/comercial/pedidos/${pid}/concluir`);
      toast.success(`Concluído! Bônus: ${fmtMoney(data.valor_bonus)}`);
      load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    }
  };

  const copiarLink = async (p) => {
    if (!p.token_assinatura) return;
    const base = window.location.origin;
    const link = `${base}/assinar/${p.token_assinatura}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copiado");
    } catch {
      toast.error("Falha ao copiar");
    }
  };

  if (items.length === 0) {
    return (
      <Card className="p-6 text-center text-sm text-muted-foreground" data-testid="pedidos-vazio">
        Nenhum pedido criado para este lead ainda.
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="pedidos-list">
      {items.map((p) => {
        const s = STATUS_LABEL[p.status] || STATUS_LABEL.rascunho;
        const open = anexosOpenId === p.id;
        return (
          <Card key={p.id} className="p-4" data-testid={`pedido-${p.id}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <FileText size={16} style={{ color: "#1B3A6B" }} />
                  <strong>{p.cliente_nome}</strong>
                  <Badge className={`${s.color} text-white`}>{s.label}</Badge>
                  <Badge variant="outline">{TIPO_LABEL[p.tipo_contrato]}</Badge>
                </div>
                <div className="text-xs text-muted-foreground">
                  Cliente: {p.cliente_email} · Vendedor: {p.vendedor_nome}
                </div>
                <div className="text-sm mt-1">
                  Total: <strong>{fmtMoney(p.valor_total)}</strong> · 1ª fatura: <strong>{fmtMoney(p.valor_primeira_fatura)}</strong>
                </div>
                {p.enviado_em && <div className="text-xs text-muted-foreground">Enviado: {fmtData(p.enviado_em)}</div>}
                {p.assinado_em && (
                  <div className="text-xs text-green-700">
                    ✅ Assinado por <strong>{p.assinante_nome}</strong> em {fmtData(p.assinado_em)}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-1">
                {p.status === "rascunho" && (
                  <Button
                    size="sm"
                    onClick={() => enviar(p.id)}
                    className="text-white"
                    style={{ background: "#1B3A6B" }}
                    data-testid={`pedido-enviar-${p.id}`}
                  >
                    <Mail size={12} className="mr-1" /> Enviar
                  </Button>
                )}
                {p.status === "enviado" && p.token_assinatura && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copiarLink(p)}
                    data-testid={`pedido-copiar-${p.id}`}
                  >
                    <Copy size={12} className="mr-1" /> Copiar link
                  </Button>
                )}
                {p.status === "assinado" && (
                  <Button
                    size="sm"
                    onClick={() => concluir(p.id)}
                    className="text-white"
                    style={{ background: "#16a34a" }}
                    data-testid={`pedido-concluir-${p.id}`}
                  >
                    <Trophy size={12} className="mr-1" /> Concluir
                  </Button>
                )}
                {p.contrato_bonus_id && (
                  <span className="text-xs text-green-700 flex items-center gap-1">
                    <CheckCircle2 size={12} /> Contrato gerado
                  </span>
                )}
              </div>
            </div>

            {(p.itens || []).length > 0 && (
              <div className="text-xs text-muted-foreground border-t pt-2">
                <strong>Itens:</strong>{" "}
                {p.itens.map((i, idx) => `${i.quantidade}× ${i.nome}${idx < p.itens.length - 1 ? ", " : ""}`)}
              </div>
            )}
            {p.observacoes && <div className="text-xs italic text-muted-foreground mt-1">{p.observacoes}</div>}

            <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setAnexosOpenId(open ? null : p.id)}
                data-testid={`pedido-toggle-anexos-${p.id}`}
              >
                <Paperclip size={12} className="mr-1" />
                Anexos do cliente
                {open ? <ChevronUp size={12} className="ml-1" /> : <ChevronDown size={12} className="ml-1" />}
              </Button>
              {lead && (
                <PropostaNativaForm
                  lead={lead}
                  prefillPedidoId={p.id}
                  triggerLabel="Gerar proposta deste pedido"
                  triggerIcon={<FilePlus2 size={12} className="mr-1" />}
                  forceEnable
                  onCreated={() => onChange?.()}
                />
              )}
            </div>

            {open && (
              <PedidoAnexos
                pedidoId={p.id}
                pedidoUnidadeId={p.unidade_id}
                pedidoVendedorId={p.vendedor_id}
              />
            )}
          </Card>
        );
      })}
    </div>
  );
}
